import { ICanCancelResponse } from 'src/app/model/cancellation/can-cancel-response.dto';
import { IPolicyCancelResponse } from 'src/app/model/cancellation/policy-cancel-response.dto';

/**
 * Mock fixtures for CancellationService, active while the `CancelPlan_UseMock`
 * portal setting is `"true"` — kept for local/dev use per the
 * policy-cancellation handoff doc §7, even though the real Elita-backed
 * `policy-cancellation/can-cancel` / `policy-cancellation/cancel` endpoints are
 * now delivered. One fixture per Figma result screen so every branch is
 * demoable end-to-end without a live backend.
 *
 * QA convention — the variant is selected by the LAST DIGIT of the plan
 * number passed to CancellationService:
 *   - ends in "5"  -> not cancellable (blocked screen)
 *   - ends in "9"  -> after-market cancel response
 *   - ends in "0"  -> no-refund cancel response
 *   - anything else -> cancellable / refund cancel response (default/common case)
 */
export const MOCK_CAN_CANCEL: ICanCancelResponse = { cancellable: true };
export const MOCK_CANNOT_CANCEL: ICanCancelResponse = { cancellable: false };

export const MOCK_CANCEL_REFUND: IPolicyCancelResponse = {
  cancellationDate: new Date().toISOString(),
  refund: 45.99,
  cancellationReason: { code: 'CUSTOMER_REQUEST', description: 'Customer requested cancellation' },
};

export const MOCK_CANCEL_NO_REFUND: IPolicyCancelResponse = {
  cancellationDate: new Date().toISOString(),
  refund: 0,
  cancellationReason: { code: 'CUSTOMER_REQUEST_NO_REFUND', description: 'Customer requested cancellation - not eligible for a refund' },
};

export const MOCK_CANCEL_AFTERMARKET: IPolicyCancelResponse = {
  cancellationDate: new Date().toISOString(),
  refund: 25.0,
  cancellationReason: { code: 'AFTERMARKET_REQUEST', description: 'Aftermarket cancellation request submitted' },
};

export function selectMockCanCancel(planNumber: string | null | undefined): ICanCancelResponse {
  return planNumber?.trim().endsWith('5') ? MOCK_CANNOT_CANCEL : MOCK_CAN_CANCEL;
}

export function selectMockCancelResponse(planNumber: string | null | undefined): IPolicyCancelResponse {
  const trimmed = planNumber?.trim() ?? '';
  if (trimmed.endsWith('9')) {
    return MOCK_CANCEL_AFTERMARKET;
  }
  if (trimmed.endsWith('0')) {
    return MOCK_CANCEL_NO_REFUND;
  }
  return MOCK_CANCEL_REFUND;
}
